import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ThumbsDown, MessageCircle, ShoppingBag, Heart } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/Layout";
import PostMedia from "@/components/PostMedia";
import PostComments from "@/components/PostComments";
import ReactionBar from "@/components/ReactionBar";
import ReportMenu from "@/components/ReportMenu";
import { timeAgo } from "@/lib/time";
import { tr } from "@/lib/translate";

export default function PostCard({ post, onChange, onBuy }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [disliked, setDisliked] = useState(post.disliked_by_me || false);
  const [dislikes, setDislikes] = useState(post.dislikes || 0);
  const [reactions, setReactions] = useState(post.reactions || { breakdown: {}, total: 0, my: null });
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState(null);
  const [flash, setFlash] = useState(false);
  const [hidden, setHidden] = useState(false); // author blocked from this card
  const lastTap = useRef(0);

  const isOwnTrack = post.track && user && post.track.artist_id === user.id;

  const callDislike = async () => {
    if (!user) { toast.error(tr("Sign in to interact")); return; }
    try {
      const { data } = await api.post(`/posts/${post.id}/dislike`);
      setDisliked(data.disliked);
      setDislikes(data.dislikes);
      // Removing dislike doesn't touch reactions, but adding one might (server clears like; reactions stays)
    } catch { toast.error(tr("Could not update like")); }
  };

  const handleDoubleTap = () => {
    const now = Date.now();
    if (now - lastTap.current < 320) {
      if (reactions.my !== "love") {
        api.post(`/posts/${post.id}/reactions`, { type: "love" }).then(({ data }) => setReactions(data)).catch(() => {});
      }
      setFlash(true);
      setTimeout(() => setFlash(false), 700);
      lastTap.current = 0;
      return;
    }
    lastTap.current = now;
  };

  const openComments = async () => {
    setShowComments((v) => !v);
    if (comments === null) {
      const { data } = await api.get(`/posts/${post.id}/comments`);
      setComments(data);
    }
  };

  if (hidden) return null;

  return (
    <article className="card-flat overflow-hidden anim-in" data-testid={`post-${post.id}`}>
      <div className="flex items-center gap-3 p-4">
        <Link to={`/profile/${post.user_id}`} className="flex items-center gap-3 group">
          <Avatar src={post.user_avatar} name={post.user_name} size="md" />
          <div>
            <p className="font-semibold text-white group-hover:underline">{post.user_name}</p>
            <p className="text-xs text-zinc-500 capitalize">{post.user_role} · {timeAgo(post.created_at)}</p>
          </div>
        </Link>
        {post.user_role === "artist" && (
          <span className="ml-auto chip bg-[#FF0050]/15 text-[#FF0050]">{t.auth.roleArtist}</span>
        )}
        <ReportMenu
          targetType="post"
          targetId={post.id}
          authorId={post.user_id}
          authorName={post.user_name}
          onBlocked={() => setHidden(true)}
          className={post.user_role === "artist" ? "" : "ml-auto"}
        />
      </div>

      <div className="relative" onClick={handleDoubleTap} onTouchEnd={handleDoubleTap}>
        <PostMedia post={post} onBuy={onBuy} />
        {flash && (
          <div className="pointer-events-none absolute inset-0 grid place-items-center">
            <Heart className="w-28 h-28 text-[#FF0050] fill-[#FF0050] drop-shadow-[0_0_20px_rgba(255,0,80,0.7)] animate-ping" />
          </div>
        )}
      </div>

      {post.caption && (
        <p className="px-4 pt-3 text-zinc-200 text-sm leading-relaxed whitespace-pre-wrap">{post.caption}</p>
      )}

      {isOwnTrack && post.track?.purchases_count !== undefined && (
        <div className="px-4 pt-2">
          <span className="chip bg-emerald-500/10 text-emerald-300 border border-emerald-500/20 inline-flex items-center gap-1" data-testid={`purchases-count-${post.id}`}>
            <ShoppingBag className="w-3 h-3" /> {post.track.purchases_count} {post.track.purchases_count > 1 ? "achats" : "achat"}
          </span>
        </div>
      )}

      <div className="flex items-center gap-1 p-2 sm:px-4">
        <ReactionBar postId={post.id} summary={reactions} onChange={setReactions} testid={`reaction-${post.id}`} />
        <button
          onClick={callDislike}
          className={`flex items-center gap-2 px-3 py-2 rounded-full transition-colors ${disliked ? "bg-zinc-700" : "hover:bg-white/5"}`}
          data-testid={`dislike-btn-${post.id}`}
        >
          <ThumbsDown className={`w-5 h-5 ${disliked ? "text-white fill-white" : "text-zinc-400"}`} />
          <span className="text-sm text-zinc-300">{dislikes}</span>
        </button>
        <button
          onClick={openComments}
          className="flex items-center gap-2 px-3 py-2 rounded-full hover:bg-white/5 transition-colors"
          data-testid={`comment-btn-${post.id}`}
        >
          <MessageCircle className="w-5 h-5 text-zinc-400" />
          <span className="text-sm text-zinc-300">{comments ? comments.length : post.comments_count}</span>
        </button>
        {post.track && !post.track.owned && !post.track.is_free && !isOwnTrack && (
          <button
            onClick={() => onBuy && onBuy(post.track)}
            className="ml-auto sm:hidden btn-primary py-2 px-4 text-xs"
            data-testid={`m-post-buy-${post.id}`}
          >
            {t.store.buy}
          </button>
        )}
      </div>

      {showComments && comments !== null && (
        <PostComments postId={post.id} initial={comments} onChange={onChange} />
      )}
    </article>
  );
}
